// Import mensuel de l'ISVZ (registre tchèque des marchés publics) : le navigateur lit le ZIP téléchargé à la main,
// garde les marchés de travaux utiles et les réduit à une forme compacte, sans aucun nom de personne, que le worker
// transforme en documents (apps/worker/src/radar/connectors/isvz.py).
import { JSONParser } from "@streamparser/json";
import { Unzip, UnzipInflate } from "fflate";

export const ISVZ_OPEN_DATA = "https://isvz.nipez.cz/opendata/nova";

// Petits marchés (« VZMR ») et marchés sans régime (presque tous d'anciens marchés republiés) sont laissés.
const WORKS = "Stavební práce";
const REGIMES = new Set(["Nadlimitní", "Podlimitní", "Zjednodušený"]);
// Formes de société ; sans elles, un titulaire peut être une personne (entrepreneur individuel) : jamais gardé.
// Même liste que COMPANY_FORM du worker.
const COMPANY_FORM =
  /s\.\s?r\.\s?o|spol\.\s?s\s?r\.\s?o|a\.\s?s\.|\ba\.\s?s$|akciov[áa] spole[čc]nost|spole[čc]nost s ru[čc]en[íi]m omezen[ýy]m|v\.\s?o\.\s?s|\bk\.\s?s\.|st[áa]tn[íi] podnik|\bs\.\s?p\.|\bz\.\s?s\.|dru[žz]stvo|\bSE\b|GmbH|\bAG\b|\bLtd|sp\.\s?z\s?o\.\s?o|\bKft\b|\bs\.\s?a\./i;
const TENDER_DEADLINE = /podání (nabídky|nabídek|žádost)/;

type Raw = Record<string, any>;

export type CompactParty = { name: string; ico?: string };
export type CompactLot = {
  title?: string;
  value_czk?: number;
  state?: string;
  procedure?: string;
  started?: string;
  ended?: string;
  link?: string;
  tender_deadline?: string;
  works_start?: string;
  works_end?: string;
  decided?: string;
  winners?: CompactParty[];
  contracts?: { signed?: string; value?: number; currency?: string }[];
};
export type CompactRecord = {
  id: string;
  title?: string;
  description?: string;
  cpv?: string;
  nuts?: string[];
  regime?: string;
  value_czk?: number;
  system?: string;
  buyers?: (CompactParty & { nuts?: string; profile?: string })[];
  lots?: CompactLot[];
};

const text = (value: unknown): string | undefined => {
  const cleaned =
    typeof value === "string" ? value.split(/\s+/).join(" ").trim() : "";
  return cleaned || undefined;
};
const day = (value: unknown): string | undefined =>
  typeof value === "string" && value ? value.slice(0, 10) : undefined;
const latest = (values: (string | undefined)[]): string | undefined =>
  values
    .filter((v): v is string => !!v)
    .sort()
    .at(-1);

/** Retire les champs vides, pour que chaque marché pèse le moins possible. */
const compactObject = <T extends Record<string, unknown>>(value: T): T =>
  Object.fromEntries(
    Object.entries(value).filter(
      ([, v]) =>
        v !== undefined &&
        v !== null &&
        v !== "" &&
        !(Array.isArray(v) && v.length === 0),
    ),
  ) as T;

export const isUsefulRecord = (contract: Raw): boolean =>
  contract?.druh_verejne_zakazky === WORKS &&
  REGIMES.has(contract?.rezim_verejne_zakazky);

const party = (subject: Raw | undefined): CompactParty | undefined => {
  const name = text(subject?.nazev_subjektu);
  return name && COMPANY_FORM.test(name)
    ? compactObject({ name, ico: text(subject?.ico) })
    : undefined;
};

const compactLot = (lot: Raw): CompactLot => {
  const procedure: Raw = lot?.zadavaci_postup_pro_cast ?? {};
  const result: Raw = procedure.vysledek ?? {};
  const selected: Raw[] = result.vybrani_dodavatele_zadavaciho_postupu ?? [];
  // Titulaire : sa société, sinon les sociétés du participant retenu. Le participant n'est lu que par ses
  // sociétés (`dodavatele`), jamais par la personne qui a déposé l'offre.
  const teams = new Map<string, Raw[]>(
    (procedure.ucastnici ?? []).map((participant: Raw) => [
      participant?.identifikace,
      (participant?.dodavatele ?? []).map((supplier: Raw) => supplier?.subjekt),
    ]),
  );
  const subjects = selected.flatMap((winner) =>
    text(winner?.subjekt?.nazev_subjektu)
      ? [winner.subjekt]
      : (teams.get(winner?.identifikator_ucastnika_zadavaciho_postupu) ?? []),
  );
  const deadlines = (procedure.lhuty ?? [])
    .filter((term: Raw) => TENDER_DEADLINE.test(term?.druh_lhuty ?? ""))
    .map((term: Raw) => day(term?.datum_a_cas_konce_lhuty));
  return compactObject({
    title: text(lot?.nazev_casti_verejne_zakazky),
    value_czk: lot?.predpokladana_hodnota_casti_bez_DPH_v_CZK ?? undefined,
    state: text(procedure.stav),
    procedure: text(procedure.druh_zadavaciho_postupu),
    started: day(procedure.datum_zahajeni_zadavaciho_postupu),
    ended: day(procedure.datum_ukonceni_zadavaciho_postupu),
    link: text(procedure.odkaz_na_profil),
    tender_deadline: latest(deadlines),
    works_start: day(procedure.doba_trvani?.datum_zacatku_doby_trvani),
    works_end: day(procedure.doba_trvani?.datum_konce_doby_trvani),
    decided: latest(
      selected.map((winner) =>
        day(winner?.datum_rozhodnuti_o_vyberu_dodavatele),
      ),
    ),
    winners: subjects.map(party).filter((p): p is CompactParty => !!p),
    contracts: (result.smlouva ?? []).map((contract: Raw) =>
      compactObject({
        signed: day(contract?.datum_uzavreni_smlouvy),
        value: contract?.hodnota_smluvni_ceny_bez_DPH ?? undefined,
        currency: text(contract?.hodnota_smluvni_ceny_mena),
      }),
    ),
  });
};

/** Forme compacte d'un marché (`verejna_zakazka`), lue par le worker. */
export const compactRecord = (
  contract: Raw,
  system?: string,
): CompactRecord => {
  const subject: Raw = contract.predmet ?? {};
  const buyers = (contract.zadavaci_postupy ?? []).flatMap((procedure: Raw) =>
    (procedure?.zadavatel_zadavaciho_postupu?.zadavatele ?? [])
      .filter((buyer: Raw) => text(buyer?.subjekt?.nazev_subjektu))
      .map((buyer: Raw) =>
        compactObject({
          name: text(buyer.subjekt.nazev_subjektu) as string,
          ico: text(buyer.subjekt.ico),
          nuts: text(buyer.subjekt.kod_NUTS),
          profile: text(buyer.adresa_profilu),
        }),
      ),
  );
  return compactObject({
    id: contract.identifikator_NIPEZ,
    title: text(contract.nazev_verejne_zakazky),
    description: text(subject.popis_predmetu)?.slice(0, 3000),
    cpv: text(subject.hlavni_kod_CPV),
    nuts: (subject.mista_plneni ?? [])
      .map((place: Raw) => text(place?.nuts))
      .filter(Boolean),
    regime: text(contract.rezim_verejne_zakazky),
    value_czk: contract.predpokladana_hodnota_bez_DPH_v_CZK ?? undefined,
    system: text(system),
    buyers,
    lots: (contract.casti_verejne_zakazky ?? []).map(compactLot),
  });
};

/** Mois du fichier, d'après son nom (« VZ-09-2026.zip », ou « VZ-09-2026 (1).zip » s'il a été retéléchargé). */
export const periodFromFileName = (name: string): string | undefined => {
  const match = /^VZ-(\d{2})-(\d{4})(?: \(\d+\))?\.zip$/i.exec(name.trim());
  if (!match || Number(match[1]) < 1 || Number(match[1]) > 12) return undefined;
  return `${match[2]}-${match[1]}`;
};

export type ReadProgress = {
  bytesRead: number;
  totalBytes: number;
  contracts: number;
  kept: number;
};

/**
 * Lit le ZIP du mois en flux (le JSON décompressé dépasse 1 Go) et renvoie les marchés compacts retenus.
 * Rien d'autre que ces marchés ne quitte le navigateur.
 */
export const readIsvzZip = async (
  file: File,
  onProgress: (progress: ReadProgress) => void,
): Promise<CompactRecord[]> => {
  const kept: CompactRecord[] = [];
  let contracts = 0;
  let bytesRead = 0;
  let entries = 0;
  let failure: Error | undefined;

  const parser = new JSONParser({ paths: ["$.data.*"], keepStack: false });
  parser.onValue = ({ value }) => {
    const item = value as Raw;
    contracts += 1;
    if (isUsefulRecord(item?.verejna_zakazka))
      kept.push(compactRecord(item.verejna_zakazka, item.zdroj_dat));
  };
  parser.onError = (error) => {
    failure = error;
  };

  const unzip = new Unzip((entry) => {
    if (!entry.name.toLowerCase().endsWith(".json")) return;
    entries += 1;
    entry.ondata = (error, chunk, final) => {
      if (error) failure = error;
      else if (!failure) {
        // Le lecteur se ferme de lui-même à la fin du document ; ne le refermer que s'il attend encore.
        if (!parser.isEnded) parser.write(chunk);
        if (final && !parser.isEnded) parser.end();
      }
    };
    entry.start();
  });
  unzip.register(UnzipInflate);

  const reader = file.stream().getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (failure) {
      await reader.cancel();
      throw failure;
    }
    if (done) {
      unzip.push(new Uint8Array(0), true);
      break;
    }
    unzip.push(value);
    bytesRead += value.length;
    onProgress({
      bytesRead,
      totalBytes: file.size,
      contracts,
      kept: kept.length,
    });
  }
  if (failure) throw failure;
  if (entries === 0) throw new Error("no_json");
  onProgress({
    bytesRead,
    totalBytes: file.size,
    contracts,
    kept: kept.length,
  });
  return kept;
};
